import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePlan } from '../../context/PlanContext';
import { usePillar } from '../../context/PillarContext';
import { UNIVERSAL_TOP, UNIVERSAL_GROUPS, BOTTOM_PINNED } from '../../config/pillarDefs';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import PillarComingSoon from './PillarComingSoon';
import {
    Sparkles, ChevronLeft, ChevronRight, ChevronDown, LogOut, Lock,
} from 'lucide-react';

const SECTIONS_KEY = 'crm_sidebar_sections';
const COLLAPSED_KEY = 'crm_sidebar_collapsed';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isItemVisible(item, user) {
    if (item.superAdminOnly) return user?.is_super_admin;
    if (item.adminOnly) return user?.is_admin || user?.is_super_admin;
    if (item.agentDeveloper) return user?.is_agent_developer || user?.is_admin || user?.is_super_admin;
    return true;
}

function isPathActive(path, pathname) {
    return pathname === path || (path !== '/dashboard' && pathname.startsWith(path));
}

// ─── Components ──────────────────────────────────────────────────────────────

function NavItem({ item, collapsed, onNavigate, location, checkFeature, badgeCounts }) {
    const Icon = item.icon;
    const isActive = isPathActive(item.path, location.pathname);
    const isLocked = item.feature && !checkFeature(item.feature);
    const badgeCount = item.badge && badgeCounts ? badgeCounts[item.badge] || 0 : 0;

    return (
        <NavLink
            to={item.path}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all group ${
                isActive
                    ? 'bg-stone-900 text-white'
                    : isLocked
                        ? 'text-stone-400 hover:bg-stone-100'
                        : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
            } ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? item.label : undefined}
        >
            {item.ai ? (
                <span className={`relative ${isActive ? 'text-white' : 'text-stone-500 group-hover:text-stone-700'}`}>
                    <Icon className="w-4 h-4" />
                    {!isActive && <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-amber-500 rounded-full" />}
                </span>
            ) : (
                <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-white' : isLocked ? 'text-stone-400' : 'text-stone-500 group-hover:text-stone-700'}`} />
            )}
            {!collapsed && <span className="flex-1">{item.label}</span>}
            {!collapsed && badgeCount > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {badgeCount > 99 ? '99+' : badgeCount}
                </span>
            )}
            {!collapsed && isLocked && <Lock className="w-3 h-3 text-stone-400" />}
        </NavLink>
    );
}

// Collapsed mode flyout for a group
function CollapsedGroupFlyout({ group, visibleItems, onNavigate, location, checkFeature }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [pos, setPos] = useState({ top: 0 });
    const timerRef = useRef(null);

    const GroupIcon = group.icon;
    const hasActive = visibleItems.some(item => isPathActive(item.path, location.pathname));

    const handleEnter = () => {
        clearTimeout(timerRef.current);
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setPos({ top: rect.top });
        }
        setOpen(true);
    };

    const handleLeave = () => {
        timerRef.current = setTimeout(() => setOpen(false), 150);
    };

    return (
        <div
            ref={ref}
            className="relative"
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
        >
            <div
                className={`flex items-center justify-center p-2.5 rounded-lg cursor-pointer transition-colors ${
                    hasActive ? 'bg-stone-200 text-stone-900' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'
                }`}
                title={group.label}
            >
                <GroupIcon className="w-4 h-4" />
            </div>
            {open && (
                <div
                    className="fixed z-[100] ml-1"
                    style={{ left: 64, top: pos.top }}
                    onMouseEnter={() => clearTimeout(timerRef.current)}
                    onMouseLeave={handleLeave}
                >
                    <div className="bg-white rounded-lg border border-stone-200 shadow-xl py-1.5 min-w-[180px]">
                        <div className="px-3 py-1.5 text-[10px] font-semibold tracking-wider uppercase text-stone-400">
                            {group.label}
                        </div>
                        {visibleItems.map(item => {
                            const Icon = item.icon;
                            const isActive = isPathActive(item.path, location.pathname);
                            const isLocked = item.feature && !checkFeature(item.feature);
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => { setOpen(false); onNavigate?.(); }}
                                    className={`flex items-center gap-2.5 px-3 py-1.5 text-sm transition-colors ${
                                        isActive
                                            ? 'bg-stone-100 text-stone-900 font-medium'
                                            : isLocked
                                                ? 'text-stone-400'
                                                : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                                    }`}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    <span className="flex-1">{item.label}</span>
                                    {isLocked && <Lock className="w-3 h-3 text-stone-400" />}
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// Expanded mode collapsible section group
function SectionGroup({ group, visibleItems, expanded, onToggle, collapsed, onNavigate, location, checkFeature, badgeCounts }) {
    if (collapsed) {
        return (
            <CollapsedGroupFlyout
                group={group}
                visibleItems={visibleItems}
                onNavigate={onNavigate}
                location={location}
                checkFeature={checkFeature}
            />
        );
    }

    const GroupIcon = group.icon;

    return (
        <Collapsible open={expanded} onOpenChange={onToggle}>
            <CollapsibleTrigger className="w-full flex items-center justify-between px-3 pt-4 pb-1 group/header cursor-pointer">
                <div className="flex items-center gap-2">
                    <GroupIcon className="w-3.5 h-3.5 text-stone-400 group-hover/header:text-stone-500 transition-colors" />
                    <span className="text-[10px] font-semibold tracking-wider uppercase text-stone-400 group-hover/header:text-stone-600 transition-colors">
                        {group.label}
                    </span>
                </div>
                <ChevronDown className={`w-3 h-3 text-stone-400 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                <div className="space-y-0.5 pt-1">
                    {visibleItems.map(item => (
                        <NavItem
                            key={item.path}
                            item={item}
                            collapsed={false}
                            onNavigate={onNavigate}
                            location={location}
                            checkFeature={checkFeature}
                            badgeCounts={badgeCounts}
                        />
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

// ─── Main Sidebar ────────────────────────────────────────────────────────────

export default function Sidebar({ onNavigate }) {
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(COLLAPSED_KEY) === 'true';
        } catch { return false; }
    });
    const [expandedSections, setExpandedSections] = useState(() => {
        try {
            const stored = localStorage.getItem(SECTIONS_KEY);
            if (stored) return new Set(JSON.parse(stored));
        } catch {}
        return new Set(['sales-crm', 'sales-insights', 'marketing-outreach', 'marketing-insights', 'hr-recruitment', 'hr-ai', 'hr-config', 'ai-tools', 'support']);
    });
    const [badgeCounts, setBadgeCounts] = useState({});
    const { user, logout } = useAuth();
    const { checkFeature } = usePlan();
    const { activePillar } = usePillar();
    const location = useLocation();

    // Fetch task + review badge counts
    useEffect(() => {
        const fetchBadges = async () => {
            try {
                const token = localStorage.getItem('crm_token');
                if (!token) return;
                const headers = { Authorization: `Bearer ${token}` };

                const tasksRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activities/tasks/summary`, { headers });
                if (tasksRes.ok) {
                    const data = await tasksRes.json();
                    setBadgeCounts(prev => ({ ...prev, tasks: data.overdue || 0 }));
                }

                const reviewsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agentic-reviews/stats`, { headers });
                if (reviewsRes.ok) {
                    const data = await reviewsRes.json();
                    setBadgeCounts(prev => ({ ...prev, reviews: data.pending || 0 }));
                }
            } catch {}
        };
        fetchBadges();
        const interval = setInterval(fetchBadges, 60000);
        return () => clearInterval(interval);
    }, []);

    // Persist collapsed state
    useEffect(() => {
        localStorage.setItem(COLLAPSED_KEY, collapsed);
    }, [collapsed]);

    // Persist expanded sections
    useEffect(() => {
        localStorage.setItem(SECTIONS_KEY, JSON.stringify([...expandedSections]));
    }, [expandedSections]);

    // Build combined groups: pillar sections + universal groups
    const allGroups = [
        ...(activePillar?.sections || []),
        ...UNIVERSAL_GROUPS,
    ];

    // Auto-expand section containing active path
    useEffect(() => {
        const activeGroup = allGroups.find(group =>
            group.items?.some(item => isPathActive(item.path, location.pathname))
        );
        if (activeGroup && !expandedSections.has(activeGroup.id)) {
            setExpandedSections(prev => new Set([...prev, activeGroup.id]));
        }
    }, [location.pathname, activePillar]);

    const toggleSection = useCallback((groupId) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    }, []);

    const getVisibleItems = useCallback((items) => {
        return items.filter(item => isItemVisible(item, user));
    }, [user]);

    return (
        <aside className={`h-screen border-r border-stone-200 flex flex-col transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'} flex-shrink-0 relative z-20`} style={{ backgroundColor: '#FAFAF5' }}>
            {/* Logo */}
            <div className={`h-14 flex items-center border-b border-stone-200 px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
                {!collapsed && (
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-stone-900 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-stone-900 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Nurture <span className="text-stone-500">CRM</span>
                        </span>
                    </div>
                )}
                {collapsed && (
                    <div className="w-8 h-8 bg-stone-900 rounded-lg flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                )}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
                    data-testid="sidebar-collapse-btn"
                >
                    {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </button>
            </div>

            {/* Scrollable nav area */}
            <nav className="flex-1 overflow-y-auto py-1 px-2">
                {/* Universal top-level items */}
                <div className="space-y-0.5">
                    {UNIVERSAL_TOP.map(item => (
                        <NavItem
                            key={item.path}
                            item={item}
                            collapsed={collapsed}
                            onNavigate={onNavigate}
                            location={location}
                            checkFeature={checkFeature}
                            badgeCounts={badgeCounts}
                        />
                    ))}
                </div>

                {/* Pillar-specific sections + Universal groups */}
                {activePillar?.comingSoon ? (
                    <PillarComingSoon pillar={activePillar} />
                ) : (
                    <div className={collapsed ? 'mt-3 space-y-0.5' : 'mt-1'}>
                        {allGroups.map(group => {
                            const visibleItems = getVisibleItems(group.items || []);
                            if (visibleItems.length === 0) return null;
                            return (
                                <SectionGroup
                                    key={group.id}
                                    group={group}
                                    visibleItems={visibleItems}
                                    expanded={expandedSections.has(group.id)}
                                    onToggle={() => toggleSection(group.id)}
                                    collapsed={collapsed}
                                    onNavigate={onNavigate}
                                    location={location}
                                    checkFeature={checkFeature}
                                    badgeCounts={badgeCounts}
                                />
                            );
                        })}
                    </div>
                )}
            </nav>

            {/* Bottom-pinned items */}
            <div className="border-t border-stone-100 px-2 py-2 space-y-0.5">
                {BOTTOM_PINNED
                    .filter(item => isItemVisible(item, user))
                    .map(item => (
                        <NavItem
                            key={item.path}
                            item={item}
                            collapsed={collapsed}
                            onNavigate={onNavigate}
                            location={location}
                            checkFeature={checkFeature}
                        />
                    ))}
            </div>

            {/* User */}
            <div className={`border-t border-stone-200 p-3 ${collapsed ? 'flex justify-center' : ''}`}>
                {!collapsed ? (
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-stone-200 flex-shrink-0">
                            {user?.picture ? (
                                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-stone-700 font-semibold text-xs">
                                    {user?.name?.charAt(0)}
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
                            <p className="text-xs text-stone-500 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
                        </div>
                        <button
                            onClick={logout}
                            data-testid="logout-btn"
                            className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                            title="Logout"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={logout}
                        data-testid="logout-btn-collapsed"
                        className="p-2 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                        title="Logout"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                )}
            </div>
        </aside>
    );
}
